/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 제목바 엘리먼트 클래스를 정의한다.
 *
 * @file /src/elements/title/Bar.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 14.
 */
import * as parent from '@base/Element';
import { Control, Properties as ControlProperties } from '@elements/title/Control';
import { Language, LanguageText } from '@utils/Language';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Bar.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 제목
     */
    text: LanguageText;

    /**
     * 아이콘
     */
    icon?: string;

    /**
     * 최대화허용 여부
     */
    maximizable?: boolean;

    /**
     * 최소화허용 여부
     */
    minimizable?: boolean;

    /**
     * 상위 컴포넌트 닫기여부
     */
    closable?: boolean;

    /**
     * 상위 컴포넌트 이동여부
     */
    movable?: boolean;

    /**
     * 컨트롤버튼
     */
    controls?: ControlProperties<Control>[];

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Bar extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type: string = 'title';

    /**
     * 제목
     */
    text: LanguageText;

    /**
     * 아이콘
     */
    icon: string | null = null;

    /**
     * 최대화허용 여부
     */
    maximizable: boolean;

    /**
     * 최소화허용 여부
     */
    minimizable: boolean;

    /**
     * 상위 컴포넌트 닫기여부
     */
    closable: boolean;

    /**
     * 상위 컴포넌트 이동여부
     */
    movable: boolean;

    /**
     * 컨트롤버튼
     */
    controls: Control[];

    /**
     * 아이콘 DOM
     */
    $icon: Dom = null;

    /**
     * 라벨 DOM
     */
    $text: Dom = null;

    /**
     * 컨트롤 DOM
     */
    $controls: Dom = null;

    /**
     * 제목 엘리먼트를 정의한다.
     *
     * @param properties - 엘리먼트 초기설정
     */
    constructor(public properties: Properties<Bar>) {
        super(properties);

        this.text = this.properties.text ?? '';
        this.icon = this.properties.icon ?? null;
        this.movable = this.properties.movable === true;
        this.maximizable = this.properties.maximizable === true;
        this.minimizable = this.properties.minimizable === true;
        this.closable = this.properties.closable === true;

        this.controls = [];
        for (const control of this.properties.controls ?? []) {
            this.controls.push(new Control(control, this));
        }
    }

    /**
     * 아이콘 DOM 을 가져온다.
     *
     * @returns 아이콘 DOM
     */
    $getIcon(): Dom {
        this.$icon ??= Html.icon(this.icon);

        return this.$icon;
    }

    /**
     * 라벨 DOM 을 가져온다.
     *
     * @returns 라벨 DOM
     */
    $getText(): Dom {
        this.$text ??= Html.create('h4');

        return this.$text;
    }

    /**
     * 컨트롤 DOM 을 가져온다.
     *
     * @returns 컨트롤 DOM
     */
    $getControls(): Dom {
        if (this.$controls === null) {
            this.$controls = Html.create('ul');
            this.$controls.on('pointerdown', (_, e) => {
                e.stopImmediatePropagation();
            });
        }

        return this.$controls;
    }

    /**
     * 최대화여부를 설정한다.
     *
     * @param maximizable - 최대화여부
     */
    setMaximizable(maximizable: boolean): void {
        this.maximizable = maximizable;
    }

    /**
     * 최소화여부를 설정한다.
     *
     * @param minimizable - 최소화여부
     */
    setMinimizable(minimizable: boolean): void {
        this.minimizable = minimizable;
    }

    /**
     * 이동여부를 설정한다.
     *
     * @param movable - 이동여부
     */
    setMovable(movable: boolean): void {
        this.movable = movable;
    }

    /**
     * 닫기여부를 설정한다.
     *
     * @param closable - 닫기여부
     */
    setClosable(closable: boolean): void {
        this.closable = closable;
    }

    /**
     * 아이콘을 변경한다.
     *
     * @param icon - 변경할 아이콘 (NULL 인 경우 아이콘을 제거한다.)
     */
    setIcon(icon: string | null = null): void {
        this.icon = icon;
        this.$icon = Html.icon(this.icon, this.$getIcon());
    }

    /**
     * 제목을 가져온다.
     *
     * @param 제목
     */
    getIcon(): string | null {
        return this.icon;
    }

    /**
     * 제목을 변경한다.
     *
     * @param text - 변경할 제목
     */
    async setText(text: LanguageText): Promise<void> {
        this.text = text;

        const $text = this.$getText();
        $text.html(await Language.resolveText(this.text));
    }

    /**
     * 제목을 가져온다.
     *
     * @param 제목
     */
    getText(): LanguageText {
        return this.text;
    }

    /**
     * 컨트롤을 추가한다.
     *
     * @param control - 추가할 컨트롤
     * @param index - 추가할 위치 (정의되지 않은 경우 제일 마지막에 추가한다)
     */
    async addControl(control: ControlProperties<Control>, index: number | undefined = undefined): Promise<void> {
        this.controls ??= [];
        const count = this.controls.length;

        const item = new Control(control, this);
        if (index === undefined || index >= count) {
            this.controls.push(item);
        } else if (index < 0 && Math.abs(index) >= count) {
            this.controls.unshift(item);
        } else {
            this.controls.splice(index, 0, item);
        }

        /**
         * 현재 엘리먼트가 렌더링되어 있는 경우,
         * 추가한 컨트롤을 바로 렌더링 한다.
         */
        if (this.isRendered() == true) {
            this.$getControls().append(item.$getElement());

            await item.render();
        }
    }

    /**
     * 컨트롤을 제거한다.
     *
     * @param control - 제거할 컨트롤
     * @param removeDom - 컨트롤 DOM 을 제거할지 여부 (기본값 : true)
     */
    removeControl(control: Control, removeDom: boolean = true): void {
        const index = this.controls.indexOf(control);
        if (index !== -1) {
            if (removeDom == true && control.$getElement().isConnected() == true) {
                control.$getElement().remove();
            }

            control.remove(!removeDom);
            this.controls.splice(index, 1);
        }
    }

    /**
     * 인덱스를 통해 컨트롤을 가져온다.
     *
     * @param index - 가져올 컴포넌트 인덱스
     * @returns 컨트롤
     */
    getControlAt(index: number): Control | null {
        return this.controls[index] ?? null;
    }

    /**
     * 컨트롤을 렌더링한다.
     */
    private async renderControls(): Promise<void> {
        const $control = this.$getControls();
        for (const control of this.controls) {
            $control.append(control.$getElement());
            await control.render();
        }

        if (this.minimizable === true) {
            const minimizable = new Control(
                {
                    icon: 'mi mi-minus',
                    handler: async (me) => {
                        const target: any = me.getComponent() as any;
                        if (target === null) {
                            return;
                        }

                        if (typeof target.minimize == 'function') {
                            await target.minimize();
                        }
                    },
                },
                this
            );
            $control.append(minimizable.$getElement());
            await minimizable.render();
        }

        if (this.maximizable === true) {
            let isMaximized = false;
            if (typeof (this.getComponent() as any)?.isMaximized == 'function') {
                isMaximized = (this.getComponent() as any).isMaximized() === true;
            }

            const maximizable = new Control(
                {
                    icon: isMaximized == true ? 'mi mi-compress' : 'mi mi-expand',
                    handler: async (me) => {
                        const target: any = me.getComponent() as any;
                        if (target === null) {
                            return;
                        }

                        let isMaximized = false;
                        if (typeof target.isMaximized == 'function') {
                            isMaximized = target.isMaximized() === true;
                        }

                        if (isMaximized == true) {
                            if (typeof target.restore == 'function') {
                                await target.restore();
                            }

                            me.setIcon('mi mi-expand');
                        } else {
                            if (typeof target.maximize == 'function') {
                                await target.maximize();
                            }

                            me.setIcon('mi mi-compress');
                        }
                    },
                },
                this
            );
            $control.append(maximizable.$getElement());
            await maximizable.render();
        }

        if (this.closable === true) {
            const closable = new Control(
                {
                    icon: 'mi mi-close',
                    handler: async (me) => {
                        if (typeof (me.getComponent() as any)?.close == 'function') {
                            (me.getComponent() as any).close();
                            return;
                        }

                        me.getComponent()?.hide();
                    },
                },
                this
            );
            $control.append(closable.$getElement());
            await closable.render();
        }
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        const $fragment: DocumentFragment = document.createDocumentFragment();

        this.setIcon(this.icon);
        $fragment.append(this.$getIcon().getEl());

        this.setText(this.text);
        $fragment.append(this.$getText().getEl());

        await this.renderControls();
        $fragment.append(this.$getControls().getEl());

        this.$getElement().append($fragment);

        await this.onRender();
    }

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (removeDom == true && this.$element !== null) {
            // @todo 부모 컴포넌트에 내가 지워졌음을 알린다.
            // 실제 DOM 제거는 super.remove() 에서 해줄 것이다.
        }

        this.$icon = null;
        this.$text = null;
        for (const control of this.controls) {
            control.remove(false);
        }
        this.controls = [];
        this.$controls = null;

        super.remove();
    }
}
